import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { inr } from "@/theme";
import { useAdminTheme } from "@/admin/AdminTheme";
import { ACard, AButton, Badge, AdminPageTitle } from "@/admin/ui";
import { Spinner } from "@/components/common";
import { toast } from "sonner";

export default function AdminInventory() {
  const { c } = useAdminTheme();
  const [products, setProducts] = useState(null);
  const [q, setQ] = useState("");
  const [edits, setEdits] = useState({});

  const load = () => api("/admin/products").then((ps) => { setProducts(ps.sort((a, b) => (a.stock || 0) - (b.stock || 0))); });
  useEffect(() => { load(); }, []);
  if (!products) return <Spinner full />;

  const shown = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  const save = async (id) => {
    const val = edits[id];
    if (val === undefined) return;
    try { await api(`/admin/products/${id}/stock`, { method: "PATCH", body: { stock: Number(val) } }); toast.success("Stock updated"); setEdits((e) => { const n = { ...e }; delete n[id]; return n; }); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="fade-up">
      <AdminPageTitle title="Inventory" subtitle="Quick stock updates · lowest first" />
      <div style={{ position: "relative", maxWidth: 360, marginBottom: 16 }}>
        <Search size={16} color={c.mutedText} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="inv-search"
          style={{ width: "100%", padding: "10px 12px 10px 36px", borderRadius: 10, border: `1px solid ${c.border}`, background: c.surface, color: c.onSurface, outline: "none", fontFamily: "DM Sans" }} />
      </div>
      <ACard style={{ padding: 0, overflow: "hidden" }}>
        {shown.map((p) => (
          <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", borderBottom: `1px solid ${c.divider}` }}>
            <div style={{ width: 44, height: 44, borderRadius: 8, overflow: "hidden", background: c.surfaceAlt, flexShrink: 0 }}>
              {p.images?.[0] && <img src={p.images[0]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="clamp-1" style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
              <div style={{ fontSize: 12.5, color: c.mutedText }}>{inr(p.price)}</div>
            </div>
            <div style={{ textAlign: "right", minWidth: 110 }}>
              <Badge tone={p.stock_status === "out_of_stock" ? "danger" : p.stock_status === "few_left" ? "warning" : "success"}>
                {p.stock_status === "out_of_stock" ? "Out of stock" : p.stock_status === "few_left" ? "Few left" : "In stock"}
              </Badge>
              <div style={{ fontSize: 11, color: c.mutedText, marginTop: 4 }}>issued {p.issued_stock ?? p.stock}</div>
            </div>
            <input data-testid={`inv-input-${p.id}`} type="number" value={edits[p.id] ?? p.stock} onChange={(e) => setEdits((ed) => ({ ...ed, [p.id]: e.target.value }))}
              style={{ width: 80, padding: "8px 10px", borderRadius: 8, border: `1px solid ${c.border}`, background: c.bg, color: c.onSurface, outline: "none", fontFamily: "DM Sans" }} />
            <AButton data-testid={`inv-save-${p.id}`} variant="soft" onClick={() => save(p.id)} disabled={edits[p.id] === undefined}>Save</AButton>
          </div>
        ))}
      </ACard>
    </div>
  );
}
